import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { 
  ArrowLeft, 
  Download, 
  Star, 
  Github,
  BookOpen,
  ExternalLink,
  Loader2,
  AlertCircle,
  Share2,
  Heart,
  Terminal,
  Copy,
  Check,
  Zap,
  ChevronRight,
  Calendar,
  User,
  Tag
} from 'lucide-react'
import SEO from '../components/SEO'
import './SkillDetail.css'

function SkillDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [skill, setSkill] = useState(null)
  const [loading, setLoading] = useState(true)
  const [liked, setLiked] = useState(false)
  const [copied, setCopied] = useState(false)
  const [categories, setCategories] = useState({})

  useEffect(() => {
    loadSkillDetail()
  }, [id])

  const loadSkillDetail = async () => {
    setLoading(true)
    try {
      // 尝试加载中文版本
      let response = await fetch('/skills.zh.json')
      let data = await response.json()
      
      // 如果中文加载失败，尝试英文
      if (!data.skills) {
        response = await fetch('/skills.json')
        data = await response.json()
      }
      
      if (data.skills && Array.isArray(data.skills)) {
        // 查找匹配的技能
        const foundSkill = data.skills.find(s => s.name === id)
        
        if (foundSkill) {
          // 添加模拟数据
          setSkill({
            ...foundSkill,
            downloads: Math.floor(Math.random() * 5000) + 100,
            rating: (Math.random() * 2 + 3).toFixed(1),
            rating_count: Math.floor(Math.random() * 500) + 10,
            author: foundSkill.name?.split('-')[0] || 'community',
            version: '1.0.0',
            updated_at: new Date().toISOString()
          })
        }
        
        // 保存分类信息
        if (data.categories_zh) {
          setCategories(data.categories_zh)
        } else if (data.categories) {
          const catMap = {}
          Object.keys(data.categories).forEach(key => {
            catMap[key] = key
          })
          setCategories(catMap)
        }
      }
    } catch (error) {
      console.error('加载技能详情失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const copyInstallCommand = () => {
    const cmd = `openclaw skills add ${skill?.name}`
    navigator.clipboard.writeText(cmd)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const getSkillDescription = () => {
    return skill?.description_zh || skill?.description || '暂无描述'
  }

  const getCategoryName = () => {
    return categories[skill?.category] || skill?.category || '其他'
  }

  if (loading) {
    return (
      <div className="skill-detail-loading">
        <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 animate-spin" />
        <p className="text-sm sm:text-base">加载中...</p>
      </div>
    )
  }

  if (!skill) {
    return (
      <div className="skill-detail-error">
        <AlertCircle className="w-12 h-12 sm:w-16 sm:h-16" />
        <h2 className="text-lg sm:text-2xl">技能不存在</h2>
        <p className="text-sm sm:text-base">该技能可能已被删除或从未存在</p>
        <Link to="/skills" className="btn-primary text-sm sm:text-base px-4 py-2 sm:px-6 sm:py-3">
          返回技能列表
        </Link>
      </div>
    )
  }

  return (
    <article className="skill-detail-container">
      <SEO 
        title={`${skill.name} - 技能详情`}
        description={getSkillDescription()}
        keywords={`${skill.name}, ${getCategoryName()}, openclaw, 一键安装`}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": skill.name,
          "operatingSystem": "OpenClaw",
          "applicationCategory": "DeveloperApplication",
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": skill.rating,
            "ratingCount": skill.rating_count
          },
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "CNY"
          }
        }}
      />
      {/* Header */}
      <div className="skill-detail-header">
        <div className="header-content">
          <button
            onClick={() => navigate('/skills')}
            className="btn-back text-sm sm:text-base px-3 py-2 sm:px-4 sm:py-2"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="hidden sm:inline">返回技能列表</span>
            <span className="sm:hidden">返回</span>
          </button>
          
          <div className="header-actions">
            <button 
              className={`btn-icon ${liked ? 'liked' : ''} w-9 h-9 sm:w-10 sm:h-10`}
              onClick={() => setLiked(!liked)}
            >
              <Heart className={`w-4 h-4 sm:w-5 sm:h-5 ${liked ? 'fill-current' : ''}`} />
            </button>
            <button className="btn-icon w-9 h-9 sm:w-10 sm:h-10">
              <Share2 className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="skill-detail-content px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        {/* Main Info */}
        <div className="skill-hero flex-col sm:flex-row">
          <div className="skill-hero-icon w-14 h-14 sm:w-20 sm:h-20">
            <Terminal className="w-7 h-7 sm:w-10 sm:h-10" />
          </div>
          
          <div className="skill-hero-info">
            <div className="skill-badges flex-wrap">
              <span className="badge category text-xs sm:text-sm">
                {getCategoryName()}
              </span>
              <span className="badge version text-xs sm:text-sm">
                v{skill.version}
              </span>
            </div>
            
            <h1 className="skill-title text-xl sm:text-2xl lg:text-3xl">{skill.name}</h1>
            <p className="skill-description text-sm sm:text-base">{getSkillDescription()}</p>
            
            <div className="skill-meta flex-wrap">
              <span className="meta-item text-xs sm:text-sm">
                <User className="w-3 h-3 sm:w-4 sm:h-4" />
                {skill.author}
              </span>
              <span className="meta-item text-xs sm:text-sm">
                <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                {new Date(skill.updated_at).toLocaleDateString('zh-CN')}
              </span>
            </div>
          </div>
          
          <div className="skill-hero-actions w-full sm:w-auto mt-4 sm:mt-0">
            <button className="btn-install-lg text-sm sm:text-base py-3 sm:py-4">
              <Zap className="w-4 h-4 sm:w-5 sm:h-5" />
              安装技能
            </button>
            
            <div className="install-command">
              <code className="text-xs sm:text-sm">openclaw skills add {skill.name}</code>
              <button 
                className="btn-copy w-8 h-8 sm:w-9 sm:h-9"
                onClick={copyInstallCommand}
                title="复制命令"
              >
                {copied ? <Check className="w-3 h-3 sm:w-4 sm:h-4" /> : <Copy className="w-3 h-3 sm:w-4 sm:h-4" />}
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="stats-grid grid-cols-1 sm:grid-cols-3">
          <div className="stat-card">
            <div className="stat-icon w-10 h-10 sm:w-12 sm:h-12">
              <Download className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div className="stat-info">
              <span className="stat-value text-lg sm:text-2xl">{skill.downloads?.toLocaleString()}</span>
              <span className="stat-label text-xs sm:text-sm">下载量</span>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon w-10 h-10 sm:w-12 sm:h-12">
              <Star className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div className="stat-info">
              <span className="stat-value text-lg sm:text-2xl">{skill.rating}</span>
              <span className="stat-label text-xs sm:text-sm">评分 ({skill.rating_count})</span>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon w-10 h-10 sm:w-12 sm:h-12">
              <Tag className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div className="stat-info">
              <span className="stat-value text-sm sm:text-lg lg:text-2xl">{getCategoryName()}</span>
              <span className="stat-label text-xs sm:text-sm">分类</span>
            </div>
          </div>
        </div>

        {/* Links */}
        <section className="skill-section">
          <h2 className="section-title text-lg sm:text-xl">相关链接</h2>
          <div className="links-grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
            {skill.github_url ? (
              <a
                href={skill.github_url}
                target="_blank"
                rel="noopener noreferrer"
                className="link-card p-4 sm:p-5"
              >
                <div className="link-icon github w-10 h-10 sm:w-12 sm:h-12">
                  <Github className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <div className="link-info">
                  <span className="link-title text-sm sm:text-base">GitHub 仓库</span>
                  <span className="link-url text-xs sm:text-sm">查看源代码</span>
                </div>
                <ExternalLink className="w-4 h-4" />
              </a>
            ) : (
              <div className="link-card disabled p-4 sm:p-5">
                <div className="link-icon w-10 h-10 sm:w-12 sm:h-12">
                  <Github className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <div className="link-info">
                  <span className="link-title text-sm sm:text-base">GitHub 仓库</span>
                  <span className="link-url text-xs sm:text-sm">暂无链接</span>
                </div>
              </div>
            )}
            
            {skill.docs_url ? (
              <a
                href={skill.docs_url}
                target="_blank"
                rel="noopener noreferrer"
                className="link-card p-4 sm:p-5"
              >
                <div className="link-icon docs w-10 h-10 sm:w-12 sm:h-12">
                  <BookOpen className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <div className="link-info">
                  <span className="link-title text-sm sm:text-base">文档</span>
                  <span className="link-url text-xs sm:text-sm">查看使用文档</span>
                </div>
                <ExternalLink className="w-4 h-4" />
              </a>
            ) : (
              <div className="link-card disabled p-4 sm:p-5">
                <div className="link-icon w-10 h-10 sm:w-12 sm:h-12">
                  <BookOpen className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <div className="link-info">
                  <span className="link-title text-sm sm:text-base">文档</span>
                  <span className="link-url text-xs sm:text-sm">暂无文档</span>
                </div>
              </div>
            )}
            
            {skill.url && (
              <a
                href={skill.url}
                target="_blank"
                rel="noopener noreferrer"
                className="link-card p-4 sm:p-5"
              >
                <div className="link-icon external w-10 h-10 sm:w-12 sm:h-12">
                  <ExternalLink className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <div className="link-info">
                  <span className="link-title text-sm sm:text-base">技能页面</span>
                  <span className="link-url text-xs sm:text-sm">访问官方页面</span>
                </div>
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>
        </section>

        {/* Install Section */}
        <section className="skill-section">
          <h2 className="section-title text-lg sm:text-xl">快速安装</h2>
          <div className="install-section p-4 sm:p-6">
            <p className="text-sm sm:text-base">在终端中运行以下命令即可安装此技能：</p>
            <div className="code-block-lg flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
              <code className="text-sm sm:text-base break-all">openclaw skills add {skill.name}</code>
              <button 
                className="btn-copy-lg text-sm py-2 px-4 sm:py-2 sm:px-4 whitespace-nowrap"
                onClick={copyInstallCommand}
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? '已复制' : '复制'}
              </button>
            </div>
          </div>
          </section>
        </div>
      </article>
    )
  }
  
  export default SkillDetail
